import { ReactNode, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import { useLocation } from 'react-router-dom'
import { cn, useAppDispatch, useAppSelector } from 'shared/lib'
import { closeMobileMenu, toggleMenu } from './model/sideBarSlice'

/**
 * Desktop rail toggle. Mirrors the panel's shape — wide horizontal bars (≡)
 * when expanded, slim vertical bars (|||) when collapsed — and instead of a flat
 * rotation it morphs through an X: the bars gather to the centre and cross (✕)
 * mid-way, then fan out into the new orientation.
 *
 * Each direction uses its own animation name (mt-c-* / mt-e-*) so toggling
 * always changes `animation-name` and the browser restarts the animation —
 * reusing one name with `reverse` would not replay on click. Reduced-motion
 * falls back to an instant swap.
 */
const RAIL_TOGGLE_CSS = `
.mtoggle{position:relative;display:block;width:20px;height:20px}
.mtoggle .bar{position:absolute;top:50%;left:50%;width:16px;height:2px;margin:-1px 0 0 -8px;border-radius:9999px;background:#C3CAD9}
.mtoggle.is-expanded .bar-top{transform:translateY(-6px)}
.mtoggle.is-expanded .bar-bot{transform:translateY(6px)}
.mtoggle.is-collapsed .bar-top{transform:translateX(-6px) rotate(90deg)}
.mtoggle.is-collapsed .bar-mid{transform:rotate(90deg)}
.mtoggle.is-collapsed .bar-bot{transform:translateX(6px) rotate(90deg)}
.mtoggle.animate.is-collapsed .bar-top{animation:mt-c-top .45s ease-in-out}
.mtoggle.animate.is-collapsed .bar-mid{animation:mt-c-mid .45s ease-in-out}
.mtoggle.animate.is-collapsed .bar-bot{animation:mt-c-bot .45s ease-in-out}
.mtoggle.animate.is-expanded .bar-top{animation:mt-e-top .45s ease-in-out}
.mtoggle.animate.is-expanded .bar-mid{animation:mt-e-mid .45s ease-in-out}
.mtoggle.animate.is-expanded .bar-bot{animation:mt-e-bot .45s ease-in-out}
@keyframes mt-c-top{0%{transform:translateY(-6px) rotate(0)}50%{transform:translate(0,0) rotate(45deg)}100%{transform:translateX(-6px) rotate(90deg)}}
@keyframes mt-c-mid{0%{transform:rotate(0);opacity:1}40%,60%{opacity:0}100%{transform:rotate(90deg);opacity:1}}
@keyframes mt-c-bot{0%{transform:translateY(6px) rotate(0)}50%{transform:translate(0,0) rotate(-45deg)}100%{transform:translateX(6px) rotate(90deg)}}
@keyframes mt-e-top{0%{transform:translateX(-6px) rotate(90deg)}50%{transform:translate(0,0) rotate(45deg)}100%{transform:translateY(-6px) rotate(0)}}
@keyframes mt-e-mid{0%{transform:rotate(90deg);opacity:1}40%,60%{opacity:0}100%{transform:rotate(0);opacity:1}}
@keyframes mt-e-bot{0%{transform:translateX(6px) rotate(90deg)}50%{transform:translate(0,0) rotate(-45deg)}100%{transform:translateY(6px) rotate(0)}}
@media (prefers-reduced-motion: reduce){.mtoggle .bar{animation:none !important}}
`

const RailToggle = ({ isExpanded }: { isExpanded: boolean }) => {
    // Only animate after the first render so the icon doesn't morph on page load.
    const hasMounted = useRef(false)
    useEffect(() => {
        hasMounted.current = true
    }, [])
    return (
        <>
            <style>{RAIL_TOGGLE_CSS}</style>
            <span
                aria-hidden="true"
                className={cn(
                    'mtoggle',
                    isExpanded ? 'is-expanded' : 'is-collapsed',
                    hasMounted.current && 'animate'
                )}
            >
                <span className="bar bar-top" />
                <span className="bar bar-mid" />
                <span className="bar bar-bot" />
            </span>
        </>
    )
}

/**
 * Mobile drawer toggle. The classic hamburger ↔ X — an X when the drawer is
 * open since tapping it closes the drawer. Plain Tailwind transitions.
 */
const DrawerToggle = ({ isOpen }: { isOpen: boolean }) => {
    const bar =
        'absolute left-0 h-0.5 w-5 rounded-full bg-inactive transition-all duration-300 ease-in-out motion-reduce:transition-none'
    return (
        <span className="relative block h-4 w-5" aria-hidden="true">
            <span
                className={cn(
                    bar,
                    isOpen ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0.5'
                )}
            />
            <span
                className={cn(
                    bar,
                    'top-1/2 -translate-y-1/2',
                    isOpen ? 'opacity-0' : 'opacity-100'
                )}
            />
            <span
                className={cn(
                    bar,
                    isOpen
                        ? 'bottom-1/2 translate-y-1/2 -rotate-45'
                        : 'bottom-0.5'
                )}
            />
        </span>
    )
}

export const Sidebar = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation(['shared'])
    const dispatch = useAppDispatch()
    const isMobileMenuOpen = useAppSelector(
        (state) => state?.globalReducer.isMobileMenuOpen
    )
    const isMenuOpen = useAppSelector(
        (state) => state?.globalReducer.isMenuOpen
    )
    const location = useLocation()
    const pointerStartX = useRef<number | null>(null)

    // Large screens get the collapsible rail (≡↔||| morph driven by isMenuOpen);
    // smaller screens are a drawer that opens/closes (hamburger↔X driven by
    // isMobileMenuOpen).
    const isLargeScreen = useMediaQuery({ minWidth: 1024 })
    const toggleActive = isLargeScreen ? isMenuOpen : isMobileMenuOpen

    useEffect(() => {
        if (isMobileMenuOpen) {
            dispatch(closeMobileMenu())
        }
    }, [location.pathname])

    const handleToggle = () => {
        if (isMobileMenuOpen) {
            dispatch(closeMobileMenu())
        } else {
            dispatch(toggleMenu())
        }
    }

    const handlePointerDown = (e: React.PointerEvent) => {
        pointerStartX.current = e.clientX
    }

    const handlePointerUp = (e: React.PointerEvent) => {
        if (pointerStartX.current === null) return
        const deltaX = e.clientX - pointerStartX.current
        if (deltaX < -50 && isMobileMenuOpen) {
            dispatch(closeMobileMenu())
        }
        pointerStartX.current = null
    }

    return (
        <>
            {/* Mobile overlay */}
            <div
                className={cn(
                    'fixed inset-0 bg-black/50 transition-opacity duration-300 ease-in-out motion-reduce:transition-none lg:hidden',
                    isMobileMenuOpen
                        ? 'opacity-100'
                        : 'pointer-events-none opacity-0'
                )}
                style={{ zIndex: 'var(--z-sidebar-overlay)' }}
                onClick={() => dispatch(closeMobileMenu())}
            />
            {/* Sidebar panel */}
            <aside
                className={cn(
                    // `visibility` transitions with the slide: it flips to
                    // hidden only once the drawer is off screen, so the
                    // closed drawer's links drop out of the tab order.
                    'flex h-screen flex-col items-start bg-background-base p-4 transition-[transform,visibility] duration-300 ease-in-out motion-reduce:transition-none lg:h-full',
                    'fixed left-0 top-0 w-[min(256px,85vw)] rounded-r-lg drop-shadow-md lg:static lg:w-full lg:translate-x-0 lg:rounded-none lg:drop-shadow-none',
                    isMobileMenuOpen
                        ? 'visible translate-x-0'
                        : 'invisible -translate-x-full lg:visible lg:translate-x-0'
                )}
                style={{ zIndex: 'var(--z-sidebar)' }}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
            >
                <button
                    type="button"
                    onClick={handleToggle}
                    aria-label={t('shared:layout.menu')}
                    aria-expanded={toggleActive}
                    className="mb-2 flex items-center justify-center rounded-lg p-2.5 transition-colors hover:bg-inactive/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inactive/40"
                >
                    {isLargeScreen ? (
                        <RailToggle isExpanded={isMenuOpen} />
                    ) : (
                        <DrawerToggle isOpen={isMobileMenuOpen} />
                    )}
                </button>
                {children}
            </aside>
        </>
    )
}
